import { formatPln } from '../catalog/money';
import type { RowAction } from '../ui/RowMenu';
import { EMAIL_STATUS_LABELS, REJECT_REASON_LABELS, type Version, type VersionSummary } from './types';

const shortDate = (iso: string) => new Intl.DateTimeFormat('pl-PL', { timeZone: 'Europe/Warsaw', day: '2-digit', month: '2-digit' }).format(new Date(iso));

/** `pdf`: the PDF shows every amount, so it needs every price field visible (spec E2 §9). */
export type Rights = { edit: boolean; send: boolean; decide: boolean; remove: boolean; pdf: boolean; /** F34: may create invoices (invoices.invoices: create) */ invoice?: boolean; /** „Rozliczenie” bez faktury: why „Wystaw fakturę” is blocked (shown, not choosable) */ invoiceBlocked?: string | null };
export type VersionHandlers = {
  settings: () => void;
  /** "Wyślij do klienta" (F30): e-mail with the PDF. */
  send: () => void;
  markSent: () => void;
  accept: () => void;
  reject: () => void;
  compare: () => void;
  remove: () => void;
  pdf: () => void;
  /** "Podgląd PDF" (P8): the PDF in a window, nothing saved or sent. */
  preview: () => void;
  /** F34 "Wystaw fakturę": a draft invoice from the accepted version. */
  invoice?: () => void;
  /** Wzór F (owner 2026-10-30): "Podgląd wydruku" — the version on A4 sheets, printed by the browser. */
  printView?: () => void;
};

/**
 * What the shown version offers (spec E2 §6.1): one main button by its state — a draft "Wyślij do klienta" (F30:
 * e-mail with the PDF; "Oznacz jako wysłaną" for a send outside the app goes under "…"; without the price fields,
 * which the PDF shows, only "Oznacz jako wysłaną"), a sent one "Zapisz akceptację", an accepted one "Pobierz PDF" —
 * and the rarer actions under "…" ("Pobierz PDF" first). On a phone only viewing, the PDF, sending and deciding
 * (editing is for the Mac).
 */
export function versionActions(v: Version, versions: VersionSummary[], rights: Rights, phone: boolean, on: VersionHandlers): { primary: RowAction | null; preview: RowAction | null; printView: RowAction | null; menu: RowAction[] } {
  let primary: RowAction | null = null;
  // owner 2026-11-04 22:36 („Blokada wysyłki”): a draft whose own lines wait for their price is not sent, nor its PDF
  // downloaded — the buttons stay, disabled, saying which; „Podgląd PDF” and „Podgląd wydruku” stay for the team
  const blocked = pricesBlocked(v);
  const off = (a: RowAction): RowAction => (blocked ? { ...a, disabled: true, title: blocked } : a);
  const markSent: RowAction = off({ label: 'Oznacz jako wysłaną', onSelect: on.markSent });
  const draftSend = v.state === 'robocza' && rights.send;
  if (draftSend) primary = rights.pdf ? off({ label: 'Wyślij do klienta', onSelect: on.send }) : markSent;
  if (v.state === 'wyslana' && rights.decide) primary = { label: 'Zapisz akceptację', onSelect: on.accept };
  const pdf: RowAction = v.state === 'robocza' ? off({ label: 'Pobierz PDF', onSelect: on.pdf }) : { label: 'Pobierz PDF', onSelect: on.pdf };
  if (v.state === 'zaakceptowana' && rights.pdf) primary = pdf;
  // F34: invoicing is the next step of an accepted version ("Pobierz PDF" then goes under "…")
  // owner 2026-11-03: an event „bez faktury” keeps the button, blocked, with the short reason
  if (v.state === 'zaakceptowana' && rights.invoice && on.invoice) primary = rights.invoiceBlocked ? { label: 'Wystaw fakturę', onSelect: () => undefined, disabled: true, title: rights.invoiceBlocked } : { label: 'Wystaw fakturę', onSelect: on.invoice };
  const menu: RowAction[] = [];
  if (rights.pdf && primary !== pdf) menu.push(pdf);
  if (draftSend && primary !== markSent) menu.push(markSent);
  if (v.state === 'robocza' && rights.edit && !phone) menu.push({ label: 'Ustawienia wersji', onSelect: on.settings });
  if (v.state === 'wyslana' && rights.decide) menu.push({ label: 'Odrzucona', onSelect: on.reject });
  if (versions.length > 1) menu.push({ label: 'Porównaj wersje', onSelect: on.compare });
  if (rights.remove && !phone) menu.push({ label: deleteVersionLabel(v, versions), danger: true, onSelect: on.remove });
  // P8 (owner 22:46 "podgląd wydruku PDF tutaj"): a visible second button, for any version, also on a phone
  const preview: RowAction | null = rights.pdf ? { label: 'Podgląd PDF', onSelect: on.preview } : null;
  // wzór F: the print view shows every amount too, so the same rights as the PDF
  const printView: RowAction | null = rights.pdf && on.printView ? { label: 'Podgląd wydruku', onSelect: on.printView } : null;
  return { primary, preview, printView, menu };
}

/** „Uzupełnij ceny pozycji własnych: …” while a draft's own lines wait for their price (the server's own message); else null. */
export function pricesBlocked(v: Pick<Version, 'state' | 'pendingPrices'>): string | null {
  return v.state === 'robocza' && v.pendingPrices && v.pendingPrices.length > 0 ? `Uzupełnij ceny pozycji własnych: ${v.pendingPrices.join(', ')}` : null;
}

/**
 * Owner 2026-10-22: "Usuń kosztorys" deletes only the version shown — "Usuń wersję v2"; the only version takes the whole
 * quote with it, and the item says so.
 */
export function deleteVersionLabel(v: Pick<Version, 'number'>, versions: readonly unknown[]): string {
  return versions.length > 1 ? `Usuń wersję v${v.number}` : `Usuń wersję v${v.number} i kosztorys`;
}

/**
 * Owner 20.10 23:37 ("przekaż wycenę ukryj w trzech kropkach", "przenoś zmiany ze sprzętówki … lepiej w trzech
 * kropkach"): giving the quote to someone and the packlist toggle are rare, so they join the "…" menu, before the
 * dangerous "Usuń wersję". `delegate` / `sync` are null when the role may not use them (the item is left out).
 * No "are you sure": the toggle saves at once, as the checkbox did.
 */
export function withQuoteExtras(
  menu: RowAction[],
  extras: {
    delegate: { responsible: string | null; onSelect: () => void } | null;
    sync: { on: boolean; enabled: boolean; onToggle: () => void } | null;
  },
): RowAction[] {
  const added: RowAction[] = [];
  if (extras.delegate) added.push({ label: extras.delegate.responsible ? `Przekaż wycenę (teraz: ${extras.delegate.responsible})…` : 'Przekaż wycenę…', onSelect: extras.delegate.onSelect });
  if (extras.sync) added.push({ label: 'Przenoś zmiany ze sprzętówki', checked: extras.sync.on, disabled: !extras.sync.enabled, onSelect: extras.sync.onToggle });
  const at = menu.findIndex((a) => a.danger);
  menu.splice(at === -1 ? menu.length : at, 0, ...added);
  return menu;
}

/** "Wersja wysłana 20.10 do Jan Kowalski", "Zaakceptowana 21.10 (mailem)"… — what happened to a frozen version. */
export function frozenText(v: Version): string {
  const note = (n: string) => (n ? ` (${n})` : '');
  const how = v.sentChannel === 'email' ? ' e-mailem' : '';
  const sent = v.sentAt ? `wysłana ${shortDate(v.sentAt)}${how}${v.sentTo ? ` do: ${v.sentTo}` : ''}${note(v.sentNote)}` : 'wysłana';
  // a draft rejected by "Rezygnacja klienta" (P8) was never sent
  const sentPart = v.sentAt ? `, ${sent}` : '';
  if (v.state === 'zaakceptowana') return `Wersja zaakceptowana${v.decidedAt ? ` ${shortDate(v.decidedAt)}` : ''}${note(v.decisionNote)}${sentPart}.`;
  const reason = v.decisionReason ? `: ${REJECT_REASON_LABELS[v.decisionReason].toLocaleLowerCase('pl-PL')}` : '';
  if (v.state === 'odrzucona') return `Wersja odrzucona${v.decidedAt ? ` ${shortDate(v.decidedAt)}` : ''}${reason}${note(v.decisionNote)}${sentPart}.`;
  return `${v.outdated ? 'Wersja nieaktualna: klient dostał już nowszą. ' : ''}Wersja ${sent}.`;
}

/** F30: where the e-mail of "Wyślij do klienta" is; null when the version was not sent by e-mail. */
export function emailStatusText(v: Pick<Version, 'sentChannel' | 'emailStatus'>): string | null {
  if (v.sentChannel !== 'email' || !v.emailStatus) return null;
  return `E-mail: ${EMAIL_STATUS_LABELS[v.emailStatus]}${v.emailStatus === 'failed' ? ' — wyślij ponownie' : ''}`;
}

/**
 * Owner 20.10 ("why can I not change the discount?"): one sentence over a frozen version — what happened to it and
 * how to change it: a new version, or the draft that exists already.
 */
export function frozenNote(v: Version, draftNumber: number | null): string {
  const what = frozenText(v).replace(/\.$/, '');
  return draftNumber !== null ? `${what} — zmiany wprowadzasz w wersji roboczej v${draftNumber}.` : `${what} — żeby zmienić (np. rabat), utwórz nową wersję.`;
}

/**
 * The line over the read-only table of a frozen version: `frozenNote` and its one button ("Nowa wersja", or
 * "Otwórz v3" when a draft exists) — the only place of "Nowa wersja" (it is not repeated under "…").
 */
export function FrozenBanner({
  version,
  versions,
  mayCreate,
  busy,
  onNewVersion,
  onOpen,
  resend,
}: {
  version: Version;
  versions: VersionSummary[];
  mayCreate: boolean;
  busy: boolean;
  onNewVersion: () => void;
  onOpen: (id: string) => void;
  /** F30 "Wyślij ponownie" of a failed e-mail (absent without the right to send). */
  resend?: { busy: boolean; error: string | null; onResend: () => void } | undefined;
}) {
  const draft = versions.find((x) => x.state === 'robocza');
  const email = emailStatusText(version);
  // third review M-b: „Nowa wersja” leaves out the lines of an accepted „Oferta łączona” (they belong to that acceptance)
  const packageLines = version.sections.flatMap((s) => s.lines).filter((l) => l.origin === 'offer');
  const packageSum = packageLines.every((l) => l.netGrosze !== undefined) ? packageLines.reduce((a, l) => a + (l.netGrosze ?? 0), 0) : null;
  return (
    <div className="q-frozen" role="status" data-state={version.outdated ? 'nieaktualna' : version.state}>
      <p>{frozenNote(version, draft ? draft.number : null)}</p>
      {email && (
        <p className="q-email-status" data-status={version.emailStatus ?? undefined} data-feature-id="quotes.send">
          {email}
          {version.emailStatus === 'failed' && resend && (
            <button className="btn small" type="button" disabled={resend.busy} onClick={resend.onResend}>
              Wyślij ponownie
            </button>
          )}
          {resend?.error && <span className="field-error"> {resend.error}</span>}
        </p>
      )}
      {packageLines.length > 0 && !draft && mayCreate && (
        <p className="hint" data-feature-id="quotes.offers">
          Nowa wersja nie przejmie linii oferty łączonej{packageSum !== null ? ` (razem ${formatPln(packageSum)})` : ''} — rabat pakietowy należy do akceptacji tamtej oferty.
        </p>
      )}
      {draft ? (
        <button className="btn small" type="button" onClick={() => onOpen(draft.id)}>
          Otwórz v{draft.number}
        </button>
      ) : (
        mayCreate && (
          <button className="btn small" type="button" disabled={busy} onClick={onNewVersion}>
            Nowa wersja
          </button>
        )
      )}
    </div>
  );
}
